import React, { useCallback, useState } from 'react';
import JSON5 from 'json5';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import TrueImpactFileUploadWithNotes, {
  indicatorAcceptedFileString,
  indicatorAllowedFileExtensions,
  indicatorMaxFileSize,
} from 'shared/design-system/components/TrueImpactFileUploadWithNotes';
import { fileUpload, IndicatorDetails, SectionIndicators } from '../utils';
import { IndicatorSectionKey } from '../../ImpactIndicator/utils';

export const indicatorFileDescriptionInitialState = (
  indicatorDetails: IndicatorDetails,
): string => {
  const f = indicatorDetails.file;
  if (!f) return '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((f as any).description) return (f as any).description;
  if (typeof f === 'string') {
    try {
      const { description } = JSON5.parse(f);
      return description;
    } catch (e) {
      return f;
    }
  }
  return '';
};

export const indicatorFileUploadInitialState = (
  indicatorDetails: IndicatorDetails,
) => (): TrueImpactFile[] => {
  const single = indicatorDetails.file as string | TrueImpactFile | undefined;
  if (!single) return [];
  if (typeof single === 'string') {
    try {
      const { fileName } = JSON5.parse(single);
      if (fileName)
        return Array.isArray(fileName) ? fileName : JSON5.parse(fileName);
    } catch (e) { /* empty */ }
  } else {
    if (single.fileName) {
      try {
        const parsed = JSON5.parse(single.fileName);
        return Array.isArray(parsed) ? parsed : [single];
      } catch {
        return [single];
      }
    }
  }
  return [];
};

export const saveIndicatorFileValue = (
  files: TrueImpactFile[],
  description: string,
): TrueImpactFile | undefined => {
  return {
    fileName: JSON.stringify(
      files.map(f => ({
        fileName: f.fileName,
        description: f.description,
        id: f.id,
        url: f.url,
      })),
    ),
    description,
  };
};

export const getIndicatorFileValue = (
  files: TrueImpactFile[],
  description: string,
): TrueImpactFile | undefined => {
  return saveIndicatorFileValue(files, description);
};

export const updateIndicatorFileValue = (
  files: TrueImpactFile[],
  description: string,
  selectedIndex: number,
  selectedSectionKey: IndicatorSectionKey,
) => (prevState: SectionIndicators) => ({
  ...prevState,
  [selectedSectionKey]: prevState[selectedSectionKey].map((item, index) =>
    index === selectedIndex
      ? {
          ...item,
          file: getIndicatorFileValue(files, description),
        }
      : item,
  ),
});

interface IndicatorFileUploadRenderProps {
  reportId: string | number;
  autoFocus?: boolean;
  description: string;
  className?: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  files: TrueImpactFile[];
  setFiles: React.Dispatch<React.SetStateAction<TrueImpactFile[]>>;
}

const IndicatorFileUploadRender: React.FC<IndicatorFileUploadRenderProps> = ({
  reportId,
  description,
  className,
  setDescription,
  files,
  autoFocus = true,
  setFiles,
}) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  const handleDelete = useCallback((fileName: string) => {
    setFiles(prev => prev.filter(f => f.fileName !== fileName));
  }, []);

  const handleFileUpload = useCallback(
    async (file: File | undefined) => {
      if (!file || files.length >= 10) return;

      const alreadyExists = files.some(f => f.fileName === file.name);
      if (alreadyExists) {
        setUploadError(`"${file.name}" has already been uploaded.`);
        return;
      }
      setUploadError(undefined);
      setIsUploading(true);

      const uploadResult = await fileUpload(reportId, file);
      if (uploadResult.success) {
        setFiles(prev => [
          ...prev,
          {
            ...uploadResult,
            file,
            id: uploadResult?.id,
            url: uploadResult?.url || '',
            fileName: file.name,
            description,
            error: undefined,
          },
        ]);
      } else {
        setUploadError('Something went wrong. Please try refreshing the page.');
      }
      setIsUploading(false);
    },
    [reportId, files.length, description],
  );

  return (
    <TrueImpactFileUploadWithNotes
      files={files}
      autoFocus={autoFocus}
      setFiles={setFiles}
      acceptedFileString={indicatorAcceptedFileString}
      maxFileSize={indicatorMaxFileSize}
      allowedFileExtensions={indicatorAllowedFileExtensions}
      placeholder="These are my assumptions and sources..."
      handleFileUpload={handleFileUpload}
      handleDelete={handleDelete}
      description={description}
      setDescription={setDescription}
      className={className}
      isUploading={isUploading}
      errorMessage={uploadError}
    />
  );
};

export default IndicatorFileUploadRender;
